import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const GlobalSettingsContext = createContext(null);

// Default branding settings
const DEFAULT_BRANDING = {
  navColor: '#1e293b',      // Slate-800
  navTextColor: '#ffffff',  // White text for dark nav
  headerColor: '#0f172a',   // Slate-900
  headerTextColor: '#ffffff', // White text for dark header
  accentColor: '#3b82f6',   // Blue-500
  fontFamily: 'Inter'
};

// Available font options
export const FONT_OPTIONS = [
  { value: 'Inter', label: 'Inter (Default)' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Source Sans Pro', label: 'Source Sans Pro' },
  { value: 'Nunito', label: 'Nunito' }
];

export const GlobalSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    currency: 'USD',
    timezone: 'UTC',
    date_format: 'DD/MM/YYYY',
    fiscal_year_start_month: 1,
    voice_note_max_duration: 60,
    branding: DEFAULT_BRANDING
  });
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    // Get saved theme from localStorage or default to 'light'
    return localStorage.getItem('crm-theme') || 'light';
  });

  // Apply theme to document - this is the key part
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
      document.body.classList.add('dark');
      // Also set a data attribute for additional styling hooks
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark');
      document.body.classList.remove('dark');
      root.setAttribute('data-theme', 'light');
    }
    
    localStorage.setItem('crm-theme', theme);
    console.log('Theme applied:', theme); // Debug log
  }, [theme]);

  // Apply branding CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const branding = settings.branding || DEFAULT_BRANDING;
    
    // Set CSS custom properties
    root.style.setProperty('--nav-color', branding.navColor);
    root.style.setProperty('--header-color', branding.headerColor);
    root.style.setProperty('--accent-color', branding.accentColor);
    root.style.setProperty('--font-family', `'${branding.fontFamily}', system-ui, sans-serif`);
    
    // Apply font family directly to body and html
    document.body.style.fontFamily = `'${branding.fontFamily}', system-ui, sans-serif`;
    root.style.fontFamily = `'${branding.fontFamily}', system-ui, sans-serif`;
    
    console.log('Branding applied:', branding); // Debug log
  }, [settings.branding]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/settings/global');
      setSettings({
        ...response.data,
        fiscal_year_start_month: response.data.fiscal_year_start_month || 1,
        voice_note_max_duration: response.data.voice_note_max_duration || 60,
        branding: {
          ...DEFAULT_BRANDING,
          ...(response.data.branding || {})
        }
      });
    } catch (error) {
      console.error('Failed to fetch global settings:', error);
      // Keep defaults if fetch fails
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch if user is logged in (token exists)
    const token = localStorage.getItem('token');
    if (token) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [fetchSettings]);

  const updateSettings = async (newSettings) => {
    try {
      const response = await api.put('/settings/global', newSettings);
      setSettings({
        ...response.data,
        fiscal_year_start_month: response.data.fiscal_year_start_month || 1,
        voice_note_max_duration: response.data.voice_note_max_duration || 60,
        branding: {
          ...DEFAULT_BRANDING,
          ...(response.data.branding || {})
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const refreshSettings = () => {
    fetchSettings();
  };

  const toggleTheme = () => {
    setTheme(prev => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      console.log('Toggling theme from', prev, 'to', newTheme); // Debug log
      return newTheme;
    });
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => ({ 
    settings, 
    loading, 
    updateSettings, 
    refreshSettings,
    currency: settings.currency,
    timezone: settings.timezone,
    dateFormat: settings.date_format,
    fiscalYearStartMonth: settings.fiscal_year_start_month || 1,
    voiceNoteMaxDuration: settings.voice_note_max_duration || 60,
    branding: settings.branding || DEFAULT_BRANDING,
    theme,
    toggleTheme,
    setTheme
  }), [settings, loading, theme]);

  return (
    <GlobalSettingsContext.Provider value={contextValue}>
      {children}
    </GlobalSettingsContext.Provider>
  );
};

export const useGlobalSettings = () => {
  const context = useContext(GlobalSettingsContext);
  if (!context) {
    // Return defaults if used outside provider
    return {
      settings: { currency: 'USD', timezone: 'UTC', date_format: 'DD/MM/YYYY', fiscal_year_start_month: 1, voice_note_max_duration: 60, branding: DEFAULT_BRANDING },
      loading: false,
      currency: 'USD',
      timezone: 'UTC',
      dateFormat: 'DD/MM/YYYY',
      fiscalYearStartMonth: 1,
      voiceNoteMaxDuration: 60,
      branding: DEFAULT_BRANDING,
      theme: 'light',
      toggleTheme: () => {},
      setTheme: () => {}
    };
  }
  return context;
};

export default GlobalSettingsContext;
